<template>
  <b-modal
    :visible="visible"
    :title="`Asistencia — ${employeeName} — ${recordDate}`"
    size="md"
    centered
    hide-footer
    @hide="$emit('close')"
  >
    <div v-if="dayData && dayData.isAutoDetected" class="alert alert-warning alert-sm mb-3">
      <i class="ri-information-line mr-1"></i>
      Este día fue marcado automáticamente como <strong>{{ autoLabel }}</strong>. Puede modificarlo.
    </div>

    <form @submit.prevent="save" novalidate>

      <!-- Estado -->
      <div class="form-group">
        <label class="label-form">Estado <span class="text-danger">*</span></label>
        <select class="form-control" v-model="form.attendanceStatus" required>
          <option value="">Seleccionar estado</option>
          <option v-for="s in statusOptions" :key="s.key" :value="s.key">{{ s.label }}</option>
        </select>
        <div v-if="submitted && !form.attendanceStatus" class="form-error">El estado es requerido</div>
      </div>

      <!-- Horas (solo si ASISTIO) -->
      <template v-if="form.attendanceStatus === 'ASISTIO'">
        <div class="row">
          <div class="col-6">
            <div class="form-group">
              <label class="label-form">Hora de entrada</label>
              <input type="time" class="form-control" v-model="form.entryTime" @change="recalculate" />
            </div>
          </div>
          <div class="col-6">
            <div class="form-group">
              <label class="label-form">Hora de salida</label>
              <input type="time" class="form-control" v-model="form.exitTime" @change="recalculate" />
            </div>
          </div>
        </div>

        <div v-if="calculation" class="calc-preview mb-3">
          <i class="ri-time-line mr-1 text-primary"></i>
          <span v-if="calculation.tardiness !== null">Tardanza: <strong>{{ calculation.tardiness }} min</strong></span>
          <span v-if="calculation.hoursWorked !== null" class="ml-3">Horas trabajadas: <strong>{{ calculation.hoursWorked }}h</strong></span>
        </div>
      </template>

      <!-- Observaciones -->
      <div class="form-group">
        <label class="label-form">Observaciones</label>
        <textarea class="form-control" v-model="form.observations" rows="2" placeholder="Opcional..."></textarea>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="button button-light mr-2" @click="$emit('close')">Cancelar</button>
        <button type="submit" class="button button-primary" :disabled="saving">
          <span v-if="saving"><i class="ri-loader-4-line"></i> Guardando...</span>
          <span v-else>Guardar</span>
        </button>
      </div>

    </form>
  </b-modal>
</template>

<script>
import { Alert } from 'wize-admin'
import { AttendanceService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'AttendanceEditModal',

  props: {
    visible:      { type: Boolean, default: false },
    employeeName: { type: String,  default: '' },
    employeeId:   { type: Number,  default: null },
    recordDate:   { type: String,  default: '' },
    dayData:      { type: Object,  default: null },
  },

  data() {
    return {
      saving:    false,
      submitted: false,
      form: {
        attendanceStatus: '',
        entryTime:        '',
        exitTime:         '',
        observations:     '',
      },
      statusOptions: Object.values(Constants.AttendanceStatus),
      calculation: null,
    }
  },

  computed: {
    autoLabel() {
      if (!this.dayData) return ''
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === this.dayData.status)
      return found ? found.label : this.dayData.status
    },
  },

  watch: {
    visible(val) {
      if (val) this.init()
    },
  },

  methods: {
    init() {
      this.submitted = false
      this.calculation = null
      if (this.dayData) {
        this.form.attendanceStatus = this.dayData.status    || ''
        this.form.entryTime        = this.dayData.entryTime || ''
        this.form.exitTime         = this.dayData.exitTime  || ''
        this.form.observations     = this.dayData.observations || ''
      } else {
        this.form = { attendanceStatus: '', entryTime: '', exitTime: '', observations: '' }
      }
    },

    recalculate() {
      const { entryTime, exitTime } = this.form
      if (!entryTime || !exitTime) { this.calculation = null; return }

      const toMin = t => {
        const [h, m] = t.split(':').map(Number)
        return h * 60 + m
      }
      const diff = toMin(exitTime) - toMin(entryTime)
      if (diff <= 0) { this.calculation = null; return }

      this.calculation = {
        hoursWorked: (diff / 60).toFixed(2),
        tardiness:   null,
      }
    },

    async save() {
      this.submitted = true
      if (!this.form.attendanceStatus) return

      try {
        this.saving = true
        await AttendanceService.saveRecord({
          employeeId:       this.employeeId,
          recordDate:       this.recordDate,
          attendanceStatus: this.form.attendanceStatus,
          entryTime:        this.form.entryTime   || null,
          exitTime:         this.form.exitTime     || null,
          observations:     this.form.observations || null,
        })
        Alert.success('Asistencia guardada correctamente')
        this.$emit('saved')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al guardar la asistencia')
      } finally {
        this.saving = false
      }
    },
  },

  created() {
    if (this.visible) this.init()
  },
}
</script>

<style scoped lang="stylus">
.calc-preview
  background #f8f9fa
  border 1px solid #dee2e6
  border-radius 4px
  padding 8px 12px
  font-size 13px

.alert-sm
  padding 8px 12px
  font-size 13px
</style>
